'use client'

import { useState } from 'react'

import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { reuploadItem as reuploadItemRequest } from '@marketplace-web/order-details/order-details-data'
import { ResponseCode } from '@marketplace-web/core-api/api-client-util'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import { clickEvent } from '@marketplace-web/conversation-reupload-item/conversation-reupload-item-data'

type Props = ConversationActionProps

const ReuploadItem = ({ primary, title, inverse, theme, size }: Props) => {
  const { track } = useTracking()

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id

  const [buttonTitle, setButtonTitle] = useState<string | null>(title)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isItemUploading, setIsItemUploading] = useState(false)

  const trackButtonClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        screen: 'message_reply',
        target: 'reupload_item',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const reuploadItem = async () => {
    trackButtonClick()

    if (!transactionId) return

    setIsDisabled(false)
    setIsItemUploading(true)

    const response = await reuploadItemRequest(transactionId)
    if (response.code === ResponseCode.Ok) {
      setIsItemUploading(false)
      setButtonTitle(title)
    }

    if ('errors' in response && response.code === ResponseCode.ValidationError) {
      // TODO: investigate where errors could be placed (instead of displaying them on button)
      setButtonTitle(response.errors[0]!.value)
      setIsDisabled(true)
      setIsItemUploading(false)
    }

    reloadPage()
  }

  if (!transactionId) return null

  return (
    <ConversationActionButton
      text={buttonTitle}
      primary={primary}
      disabled={isDisabled}
      size={size}
      theme={theme}
      inverse={inverse}
      isLoading={isItemUploading}
      onClick={reuploadItem}
      testId="conversation-actions-reupload-item"
    />
  )
}

export default ReuploadItem
